import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Container,
  ModalContainer,
} from './styles';

import useTextSelection from '../../hooks/use-text-selection';
import useOutsideClick from '../../hooks/use-outside-click';
import {
  getFirstSelectionOffset,
  getLastSelectionOffset,
  getSelectionAttributes,
  getSelectionElements,
} from '../../utils/selection-attributes';

const ModalOnHighlight = ({
  children,
  width,
  height,
  isOpen,
  onTextSelected,
  onClickOutside,
}) => {
  const [selectedText, setSelectedText] = useState(null);
  const [positionLeft, setPositionLeft] = useState(null);
  const [positionTop, setPositionTop] = useState(null);
  const containerRef = useRef(null);

  useTextSelection(() => {
    // In case we have the modal opened, we should not make any updates
    // to the current selection state.
    if (isOpen) return;

    const selection = document.getSelection();

    const txtSelection = selection.toString().trim();

    if (selection && selection.rangeCount && txtSelection) {
      const {
        entityId: startId,
        entityType: startEntity,
        node,
      } = getSelectionAttributes(selection.anchorNode);

      const selectionNodeElements = getSelectionElements(node);

      const {
        entityId: endId,
      } = getSelectionAttributes(selection.focusNode);

      if (!startId || !endId) return;

      const range0 = selection.getRangeAt(0);
      const {
        left: rangeParentLeft,
      } = range0.startContainer.parentElement.getBoundingClientRect();

      const {
        bottom: highlightBottom,
        top: highlightTop,
      } = range0.getBoundingClientRect();

      if (highlightBottom + height < window.innerHeight) {
        setPositionTop(highlightBottom);
      } else if (highlightTop - height < 0) {
        if (highlightBottom + height > window.innerHeight) {
          setPositionTop(window.innerHeight / 3);
        } else {
          setPositionTop(highlightTop);
        }
      } else if (highlightTop - height < 50) {
        setPositionTop(50);
      } else {
        setPositionTop(highlightTop - height);
      }

      setPositionLeft(rangeParentLeft - width);

      let charEnd = getFirstSelectionOffset(selection, selectionNodeElements);
      let charStart = getLastSelectionOffset(selection, selectionNodeElements);

      if (charStart === charEnd || charEnd < 0) {
        setSelectedText(null);
        return;
      }

      if (charStart > charEnd) {
        const tmp = charStart;
        charStart = charEnd;
        charEnd = tmp;
      }

      setSelectedText({
        id: startId,
        entity: startEntity,
        charStart,
        charEnd,
      });
      return;
    }

    setSelectedText(null);
  }, [setSelectedText, setPositionTop, setPositionLeft, isOpen]);

  useEffect(() => {
    onTextSelected(selectedText);
  }, [selectedText, onTextSelected]);

  useOutsideClick(containerRef, () => {
    if (isOpen) {
      setSelectedText(null);
      onClickOutside();
    }
  });

  return (
    <Container
      ref={containerRef}
      left={positionLeft}
      top={positionTop}
      show={isOpen}
    >
      {selectedText && (
        <ModalContainer
          width={width}
          height={height}
        >
          {children}
        </ModalContainer>
      )}
    </Container>
  );
};

ModalOnHighlight.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  isOpen: PropTypes.bool,
  onTextSelected: PropTypes.func,
  onClickOutside: PropTypes.func,
  children: PropTypes.node,
};

ModalOnHighlight.defaultProps = {
  width: 330,
  height: 400,
  isOpen: false,
  onTextSelected: (sel) => {
    console.log('[ModalOnHighlight] Selected: ', sel);
  },
  onClickOutside: () => {
    console.log('[ModalOnHighlight] Close');
  },
  children: <></>,
};

export default ModalOnHighlight;
